import { useState, useEffect } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiGet } from '../../../lib/api';

interface LeaderboardEntry {
  rank: number;
  customerId: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  score: number;
  tierCode: string | null;
}

interface LeaderboardResponse {
  top: LeaderboardEntry[];
  myRank: { rank: number; score: number } | null;
}

interface LoyaltyLeaderboardFullProps {
  title?: string;
  showTabs?: boolean;
  maxEntries?: number;
}

type Period = 'weekly' | 'monthly';

const PERIOD_LABELS: Record<Period, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
};

function medalColor(rank: number): string | null {
  if (rank === 1) return '#FFD700';
  if (rank === 2) return '#C0C0C0';
  if (rank === 3) return '#CD7F32';
  return null;
}

function AvatarSmall({ firstName, lastName, avatarUrl }: { firstName: string; lastName: string; avatarUrl: string | null }) {
  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || '?';

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={`${firstName} ${lastName}`}
        className="w-8 h-8 rounded-full object-cover border border-[var(--color-surface-border)]"
      />
    );
  }

  return (
    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold bg-[var(--color-primary)] text-white">
      {initials}
    </div>
  );
}

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#127942;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to view the leaderboard
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to see how you rank against other members.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

export function LoyaltyLeaderboardFullBlock({ props }: { props: LoyaltyLeaderboardFullProps }) {
  const { data, loading: loyaltyLoading } = useLoyalty();
  const showTabs = props.showTabs ?? true;
  const maxEntries = props.maxEntries ?? 10;
  const title = props.title ?? 'GLOBAL TOP 10';

  const [period, setPeriod] = useState<Period>('monthly');
  const [leaderboard, setLeaderboard] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    setLoading(true);

    apiGet<LeaderboardResponse>(`/customer/loyalty/leaderboard?programId=${data.programId}&periodType=${period}`)
      .then((res) => {
        if (!cancelled) setLeaderboard(res);
      })
      .catch(() => {
        if (!cancelled) setLeaderboard(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [data, period]);

  if (loyaltyLoading) {
    return (
      <div className="loyalty-card p-8 animate-pulse">
        <div className="h-6 w-48 bg-[var(--color-surface-border)] rounded mb-6" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 bg-[var(--color-surface-border)] rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;

  const entries = leaderboard?.top.slice(0, maxEntries) ?? [];
  const customerRank = leaderboard?.myRank;

  return (
    <div className="loyalty-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="loyalty-heading text-lg sm:text-xl text-[var(--color-text)]">
          {title}
        </h2>
      </div>

      {/* Tabs */}
      {showTabs && (
        <div className="flex gap-2 mb-6">
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                period === p
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="space-y-3 animate-pulse">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-10 bg-[var(--color-surface-border)] rounded" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[var(--color-text-muted)] text-xs uppercase tracking-wider border-b border-[var(--color-surface-border)]">
                <th className="text-left py-2 pr-2 w-12">Rank</th>
                <th className="text-left py-2 pr-2">Athlete</th>
                <th className="text-right py-2 pr-2">Loyalty Points</th>
                <th className="text-right py-2">Tier</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const medal = medalColor(entry.rank);
                const isCurrentUser = customerRank && entry.rank === customerRank.rank;
                return (
                  <tr
                    key={entry.customerId}
                    className={`border-b border-[var(--color-surface-border)] last:border-0 transition-colors ${
                      isCurrentUser ? 'bg-[var(--color-primary)]/10' : ''
                    }`}
                  >
                    <td className="py-3 pr-2">
                      <span
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold"
                        style={medal
                          ? { backgroundColor: medal, color: '#fff' }
                          : { color: 'var(--color-text-muted)' }
                        }
                      >
                        {entry.rank}
                      </span>
                    </td>
                    <td className="py-3 pr-2">
                      <div className="flex items-center gap-2">
                        <AvatarSmall
                          firstName={entry.firstName}
                          lastName={entry.lastName}
                          avatarUrl={entry.avatarUrl}
                        />
                        <span className="font-medium text-[var(--color-text)] truncate">
                          {entry.firstName} {entry.lastName}
                        </span>
                        {entry.rank === 1 && (
                          <span className="loyalty-tag-skew text-[10px] font-bold px-2 py-0.5 rounded bg-[var(--color-primary)] text-white">
                            LEADER
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-2 text-right font-bold text-[var(--color-primary)]">
                      {entry.score.toLocaleString()}
                    </td>
                    <td className="py-3 text-right">
                      {entry.tierCode && (
                        <span className="loyalty-tag-skew text-[10px] font-bold px-2 py-0.5 rounded-full text-[var(--color-text-muted)] bg-[var(--color-surface)]">
                          {entry.tierCode === 'pro' ? 'PRO' : entry.tierCode.toUpperCase()}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {entries.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-8 text-center text-[var(--color-text-muted)] text-sm">
                    No leaderboard data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Current user rank footer */}
      {customerRank?.rank && !entries.some((e) => e.rank === customerRank.rank) && (
        <div className="mt-4 p-3 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-between text-sm">
          <span className="text-[var(--color-text)]">
            Your rank: <span className="font-bold">#{customerRank.rank}</span>
          </span>
          <span className="font-bold text-[var(--color-primary)]">
            {customerRank.score.toLocaleString()} PTS
          </span>
        </div>
      )}

      {/* Footer link */}
      <div className="mt-4 text-center">
        <a
          href="/leaderboard"
          className="text-sm font-semibold text-[var(--color-primary)] hover:underline"
        >
          View full leaderboard &rarr;
        </a>
      </div>
    </div>
  );
}
